import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ResellerTaxExemptType } from '@/types/gql-op-types';
import Switch from '../../components/Switch/Switch';
import { useToast } from '../../components/Toast/ToastContext';
import {
  rescueAccountInfoQuery,
  RescueAccountInfoData,
  rescueSetRegistrationEmailOptInMutation,
  RescueSetRegistrationEmailOptInData,
} from '../../lib/accountQueries';
import {
  rescueShelterluvStatusQuery,
  rescueSetShelterluvEmailUpdateOptInMutation,
  RescueShelterluvStatusData,
  RescueSetShelterluvEmailUpdateOptInData,
} from '../../lib/integrationQueries';
import { adaptAccountInfo, formatContactName, formatUsPhone } from '../../lib/accountAdapters';
import {
  AccountSection,
  trackAccountEditOpened,
  trackShelterLuvEmailUpdatesToggled,
  trackRegistrationEmailsToggled,
} from '../../analytics';
import AccountSectionCard, { DetailRow } from './AccountSectionCard';
import EditOrganizationModal from './EditOrganizationModal';
import EditPrimaryContactModal from './EditPrimaryContactModal';
import EditAddressModal from './EditAddressModal';
import CardExemptionDocuments from './CardExemptionDocuments';
import CardApiKey from './CardApiKey';
import styles from './Account.module.scss';

const ORG_TYPE_LABELS: Record<ResellerTaxExemptType, string> = {
  [ResellerTaxExemptType.REGISTERED_501C3]: 'Registered 501c3',
  [ResellerTaxExemptType.GOVERNMENT_ENTITY]: 'Municipal / Government Shelter',
};

function OrgTypeTag({ type }: { type: ResellerTaxExemptType }) {
  return (
    <span className={styles.typeTag}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
      {ORG_TYPE_LABELS[type]}
    </span>
  );
}

export default function Account() {
  const { showToast } = useToast();

  // errorPolicy 'all' — see the query's doc comment (non-null sub-fields on
  // nullable reseller records would otherwise null out all of `data`).
  const { data, loading } = useQuery<RescueAccountInfoData>(rescueAccountInfoQuery, { errorPolicy: 'all' });
  const view = adaptAccountInfo(data);

  // ShelterLuv email opt-in — same wiring as the Integrations page; the
  // shared Apollo cache keeps both surfaces in sync.
  const { data: shelterluvData } = useQuery<RescueShelterluvStatusData>(rescueShelterluvStatusQuery);
  const shelterluvConfig = shelterluvData?.rescueShelterluvStatus;
  const [setEmailUpdateOptIn, { loading: emailUpdatesSaving }] = useMutation<RescueSetShelterluvEmailUpdateOptInData>(
    rescueSetShelterluvEmailUpdateOptInMutation,
  );
  const [setRegistrationEmailOptIn, { loading: registrationEmailsSaving }] =
    useMutation<RescueSetRegistrationEmailOptInData>(rescueSetRegistrationEmailOptInMutation);

  const [openModal, setOpenModal] = useState<AccountSection | null>(null);

  function openEdit(section: AccountSection) {
    trackAccountEditOpened(section);
    setOpenModal(section);
  }

  function closeEdit() {
    setOpenModal(null);
  }

  async function handleToggleEmailUpdates(optIn: boolean) {
    try {
      await setEmailUpdateOptIn({
        variables: { optIn },
        // rescueShelterluvStatus is a separate root query, so Apollo can't merge
        // the mutation payload into it automatically. Write the value we set.
        update: (cache) => {
          cache.updateQuery<RescueShelterluvStatusData>({ query: rescueShelterluvStatusQuery }, (prev) =>
            prev?.rescueShelterluvStatus
              ? { rescueShelterluvStatus: { ...prev.rescueShelterluvStatus, emailUpdateOptIn: optIn } }
              : prev,
          );
        },
      });
      trackShelterLuvEmailUpdatesToggled(optIn);
      showToast(optIn ? 'Registration emails turned on' : 'Registration emails turned off');
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Failed to update email preferences: ${detail}`);
    }
  }

  async function handleToggleRegistrationEmails(optIn: boolean) {
    try {
      await setRegistrationEmailOptIn({ variables: { optIn } });
      trackRegistrationEmailsToggled(optIn);
      showToast(optIn ? 'Registration emails turned on' : 'Registration emails turned off');
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Failed to update email preferences: ${detail}`);
    }
  }

  if (!view) {
    return (
      <div>
        <h1>Manage Account</h1>
        <div className={styles.loading}>{loading ? 'Loading…' : 'Account details are unavailable right now.'}</div>
      </div>
    );
  }

  const { organization, primaryContact, address, shippingAddress, isReseller, registrationEmailOptIn } = view;
  const canEdit = isReseller;

  return (
    <div>
      <h1>Manage Account</h1>

      <div className={styles.formWrap}>
        <AccountSectionCard
          title="Organization"
          description="This is how your rescue appears across Fi — on adoption registrations, postcards, and your dashboard."
          onEdit={() => openEdit('organization')}
        >
          <DetailRow label="Organization name" value={organization.name} />
          <DetailRow label="Organization email" value={organization.email} />
          {isReseller && <DetailRow label="Website" value={organization.website} />}
          {isReseller && (
            <DetailRow
              label="Organization type"
              value={organization.taxExemptType ? <OrgTypeTag type={organization.taxExemptType} /> : ''}
            />
          )}
        </AccountSectionCard>

        {primaryContact && (
          <AccountSectionCard
            title="Primary Contact"
            description="Who Fi reaches out to about your account, orders, and program updates."
            onEdit={canEdit ? () => openEdit('primary_contact') : undefined}
          >
            <DetailRow label="Contact name" value={formatContactName(primaryContact)} />
            <DetailRow label="Primary phone" value={formatUsPhone(primaryContact.phone)} />
            <DetailRow label="Primary email" value={primaryContact.email} />
          </AccountSectionCard>
        )}

        {address && (
          <AccountSectionCard
            title="Address"
            description="Your organization's main contact address."
            onEdit={canEdit ? () => openEdit('address') : undefined}
          >
            <DetailRow label="Street" value={[address.line1, address.line2].filter(Boolean).join(', ')} />
            <DetailRow label="City" value={address.city} />
            <DetailRow label="State" value={address.state} />
            <DetailRow label="Zip" value={address.zip} />
          </AccountSectionCard>
        )}

        {shippingAddress && (
          <AccountSectionCard
            title="Shipping Address"
            description="Where device orders and Fi Nano microchips ship. You can still change this per order at checkout."
            onEdit={canEdit ? () => openEdit('shipping_address') : undefined}
          >
            <DetailRow
              label="Street"
              value={[shippingAddress.line1, shippingAddress.line2].filter(Boolean).join(', ')}
            />
            <DetailRow label="City" value={shippingAddress.city} />
            <DetailRow label="State" value={shippingAddress.state} />
            <DetailRow label="Zip" value={shippingAddress.zip} />
          </AccountSectionCard>
        )}

        {/*
          Shown to every rescue portal user, deliberately not gated on
          `taxExemptType`: that field lives only on the chip-reseller record, so
          gating on it would hide the card from rescue users who have no reseller
          account — exactly the people who still need to submit a letter.
        */}
        <CardExemptionDocuments />

        <CardApiKey />

        {(registrationEmailOptIn !== null || shelterluvConfig?.enabled) && (
          <AccountSectionCard
            title="Email & Notifications"
            description="Control the automated emails you receive from Fi."
          >
            {registrationEmailOptIn !== null && (
              <div className={styles.toggleRow}>
                <div className={styles.toggleText}>
                  <div className={styles.toggleTitle}>
                    Email me each time one of my microchips is registered or changes owner
                  </div>
                  <div className={styles.toggleSub}>
                    Fi emails you the pet, microchip and adopter details. Turn off to stop receiving these.
                  </div>
                </div>
                <Switch
                  checked={registrationEmailOptIn}
                  onChange={handleToggleRegistrationEmails}
                  disabled={registrationEmailsSaving}
                  ariaLabel="Email me each time one of my microchips is registered or changes owner"
                />
              </div>
            )}
            {shelterluvConfig?.enabled && (
              <div
                className={`${styles.toggleRow} ${styles.toggleRowNested} ${
                  registrationEmailOptIn === false ? styles.toggleRowDisabled : ''
                }`}
              >
                <div className={styles.toggleText}>
                  <div className={styles.toggleTitle}>Include registrations imported from ShelterLuv</div>
                  <div className={styles.toggleSub}>
                    {registrationEmailOptIn === false
                      ? 'Turn on registration emails above to change this.'
                      : 'Turn off if you already track these in ShelterLuv and only want emails for other registrations.'}
                  </div>
                </div>
                <Switch
                  checked={registrationEmailOptIn !== false && shelterluvConfig.emailUpdateOptIn}
                  onChange={handleToggleEmailUpdates}
                  disabled={registrationEmailOptIn === false || emailUpdatesSaving}
                  ariaLabel="Include registrations imported from ShelterLuv"
                />
              </div>
            )}
          </AccountSectionCard>
        )}
      </div>

      <EditOrganizationModal open={openModal === 'organization'} onClose={closeEdit} view={view} />
      <EditPrimaryContactModal open={openModal === 'primary_contact'} onClose={closeEdit} view={view} />
      <EditAddressModal open={openModal === 'address'} onClose={closeEdit} view={view} variant="address" />
      <EditAddressModal
        open={openModal === 'shipping_address'}
        onClose={closeEdit}
        view={view}
        variant="shipping_address"
      />
    </div>
  );
}
